import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Container,
  Chip,
} from "@mui/material";
import { ArrowBack as BackIcon } from "@mui/icons-material";

// Helper function to get priority color
const getPriorityColor = (priority) => {
  switch (priority?.toLowerCase()) {
    case "high":
      return "#f44336";
    case "medium":
      return "#ff9800";
    case "low":
      return "#4caf50";
    default:
      return "#9e9e9e";
  }
};

const TestCasesImportedPage = ({
  testCases,
}) => {
  // const navigate = useNavigate();

  const currentTestCase = testCases?.[0] || {
    testCaseId: "No test case available",
    title: "No test case available",
    testSteps: "No test steps available",
    expectedResult: "No expected result available",
    moduleName: "General",
    priority: "Medium",
    remarks: "No remarks available",
  };

  // Default back handler
  // const defaultHandleBack = () => {
  //   navigate(`${automationPath}/test-cases`, {
  //     state: { automationType, preferences, environment, testCases },
  //   });
  // };

  // Show message if no test cases are available
  if (!testCases || testCases.length === 0) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: "#F6F3F2",
          display: "flex",
          flexDirection: "column",
          py: 4,
        }}
      >
        <Container maxWidth="lg" sx={{ width: "100%", flex: 1 }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              mb: 4,
              fontWeight: "bold",
              color: "#333",
              fontSize: "28px",
              // fontFamily: "Manrope",
            }}
          >
            Test Case Imported
          </Typography>
          <Card
            sx={{
              backgroundColor: "white",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              mb: 4,
              maxWidth: "100%",
            }}
          >
            <CardContent sx={{ p: 4, textAlign: "center" }}>
              <Typography
                variant="h6"
                sx={{
                  color: "#666",
                  // fontFamily: "Manrope",
                  mb: 2,
                }}
              >
                No test cases available. Please go back and upload test cases
                first.
              </Typography>
              {/* <Button
                variant="contained"
                startIcon={<BackIcon />}
                onClick={defaultHandleBack}
                sx={{
                  mt: 2,
                  bgcolor: "#0094FB",
                  textTransform: "none",
                  // fontFamily: "Manrope",
                  "&:hover": { bgcolor: "#0078D4" },
                }}
              >
                Go Back
              </Button> */}
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Container sx={{ width: "100%", flex: 1 }}>
      {/* Page Title */}
      <Typography
        variant="h4"
        component="h1"
        sx={{
          mb: 4,
          fontWeight: "bold",
          color: "#333",
          fontSize: "28px",
          // fontFamily: "Manrope",
        }}
      >
        Test Case Imported
      </Typography>

      {/* Main Content Card */}
      <Card
        sx={{
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          mb: 4,
          maxWidth: "100%",
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Test Case Details - Four Column Layout */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              gap: "24px",
              mb: 4,
              flexWrap: "wrap",
            }}
          >
            {/* Column 1 */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 3,
                minWidth: "200px",
              }}
            >
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#666",
                    mb: 1,
                    // fontFamily: "Manrope",
                    fontWeight: 600,
                    fontSize: "14px",
                  }}
                >
                  Test Case Title
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    // fontFamily: "Manrope",
                    color: "#333",
                    fontSize: "16px",
                  }}
                >
                  {currentTestCase.title}
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#666",
                    mb: 1,
                    // fontFamily: "Manrope",
                    fontWeight: 600,
                    fontSize: "14px",
                  }}
                >
                  Module Name
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    // fontFamily: "Manrope",
                    color: "#333",
                    fontSize: "14px",
                  }}
                >
                  {currentTestCase.moduleName}
                </Typography>
              </Box>
            </Box>

            {/* Column 2 */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 3,
                minWidth: "200px",
              }}
            >
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#666",
                    mb: 1,
                    // fontFamily: "Manrope",
                    fontWeight: 600,
                    fontSize: "14px",
                  }}
                >
                  Test Case ID
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    // fontFamily: "Manrope",
                    color: "#333",
                    fontSize: "14px",
                  }}
                >
                  {currentTestCase.testCaseId}
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#666",
                    mb: 1,
                    // fontFamily: "Manrope",
                    fontWeight: 600,
                    fontSize: "14px",
                  }}
                >
                  Priority
                </Typography>
                <Chip
                  label={currentTestCase.priority}
                  size="small"
                  sx={{
                    backgroundColor: getPriorityColor(currentTestCase.priority),
                    color: "#fff",
                    // fontFamily: "Manrope",
                    fontSize: "12px",
                    fontWeight: 500,
                  }}
                />
              </Box>
            </Box>

            {/* Column 3 */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 3,
                minWidth: "200px",
              }}
            >
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#666",
                    mb: 1,
                    // fontFamily: "Manrope",
                    fontWeight: 600,
                    fontSize: "14px",
                  }}
                >
                  Expected Result
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    // fontFamily: "Manrope",
                    color: "#333",
                    fontSize: "14px",
                  }}
                >
                  {currentTestCase.expectedResult}
                </Typography>
              </Box>
            </Box>

            {/* Column 4 */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 3,
                minWidth: "200px",
              }}
            >
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#666",
                    mb: 1,
                    // fontFamily: "Manrope",
                    fontWeight: 600,
                    fontSize: "14px",
                  }}
                >
                  Remarks
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    // fontFamily: "Manrope",
                    color: "#333",
                    fontSize: "14px",
                  }}
                >
                  {currentTestCase.remarks || "No remarks"}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Full Width Sections */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="body2"
              sx={{
                color: "#666",
                mb: 2,
                // fontFamily: "Manrope",
                fontWeight: 600,
                fontSize: "14px",
              }}
            >
              Test Steps
            </Typography>
            <Typography
              variant="body1"
              sx={{
                // fontFamily: "Manrope",
                lineHeight: 1.6,
                color: "#333",
                fontSize: "14px",
                whiteSpace: "pre-line",
              }}
            >
              {currentTestCase.testSteps}
            </Typography>
          </Box>

          {/* Custom Actions */}
          {/* {customActions && <Box sx={{ mb: 4 }}>{customActions}</Box>} */}
        </CardContent>
      </Card>
    </Container>
  );
};

export default TestCasesImportedPage;
